import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import type { WorkflowDto } from 'model/workflow';
import { workflowToXyflow, xyflowToWorkflow, type WorkflowNode } from 'model/xyflowMapping';
import { WorkflowCanvasHandle } from 'components/canvas/WorkflowCanvas';
import { RawJsonEditorModal } from 'components/modals/RawJsonEditorModal';
import {
  type RunnerState,
  type StepColorMap,
  defaultRunnerState,
} from 'components/modals/RunWorkflowModal';
import type { ViewMode } from 'state/ViewModeContext';
import type { FormatMode } from 'state/FormatContext';
import { validateWorkflow } from 'model/validation';
import { loadCachedGraph, saveCachedGraph } from 'utils/workflowCache';
import { useActionsCatalog } from 'hooks/useActionsCatalog';
import { useEditorActions } from 'hooks/useEditorActions';
import { useEditorKeyboardShortcuts } from 'hooks/useEditorKeyboardShortcuts';
import { useEditorPersistence } from 'hooks/useEditorPersistence';
import { useWorkflowGraphState } from 'hooks/useWorkflowGraphState';
import {
  EditorBody,
  EditorHeader,
  EditorStatusBar,
  EditorToolbar,
  type ContextMenuState,
  type SidebarTab,
} from 'components/workflow-editor';

import { StyledEditorLayout } from './WorkflowEditorPage.styled';

/**
 * Delay (ms) between applying the reorganized graph and calling
 * `canvasRef.current.fitView()`. Lets xyflow finish reconciling node
 * positions before we ask it to compute the bounding box.
 */
const FIT_VIEW_DELAY_MS = 50;

/**
 * Debounce window (ms) for writing the dirty graph to localStorage.
 * Keeps autosave cheap during rapid edits — only the trailing change
 * persists.
 */
const AUTOSAVE_DEBOUNCE_MS = 500;

interface OwnProps {
  workflowId: string;
  initialWorkflow: WorkflowDto;
  onBack?: () => void;
}

const WorkflowEditorPage = ({
  workflowId: initialWorkflowId,
  initialWorkflow,
  onBack,
}: OwnProps) => {
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('flow');
  const [sidebarTab, setSidebarTab] = useState<SidebarTab>('editor');
  const [stepRunColors, setStepRunColors] = useState<StepColorMap | null>(null);
  const [isRunningJob, setIsRunningJob] = useState(false);
  const [runnerState, setRunnerState] = useState<RunnerState>(() =>
    defaultRunnerState(initialWorkflowId),
  );
  const [formatMode, setFormatMode] = useState<FormatMode>('formatted');
  const [workflowNameDraft, setWorkflowNameDraft] = useState<string>(initialWorkflow.name);
  const [edgeType, setEdgeType] = useState<string>('default');
  const [showActionPalette, setShowActionPalette] = useState(false);
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [selectedRunnerFile, setSelectedRunnerFile] = useState<File | null>(null);

  const canvasRef = useRef<WorkflowCanvasHandle>(null);

  const initialState = useMemo(() => {
    const cached = loadCachedGraph(initialWorkflowId);
    return {
      initialGraph: cached ?? workflowToXyflow(initialWorkflow),
      initialDirty: cached !== null,
    };
  }, [initialWorkflow, initialWorkflowId]);

  const editor = useWorkflowGraphState(initialState);
  const { actions: availableActions } = useActionsCatalog();
  const actions = useEditorActions({ apply: editor.apply, setSelectedNodeId });

  const clearSelection = useCallback(() => setSelectedNodeId(null), []);
  const persistence = useEditorPersistence({
    initialWorkflowId,
    initialWorkflow,
    graph: editor.graph,
    resetGraph: editor.resetGraph,
    clearSelection,
  });
  const {
    workflowId,
    workflow,
    setWorkflow,
    isSaving,
    error,
    statusMessage,
    showJsonEditor,
    openJsonEditor,
    closeJsonEditor,
    handleReset,
    handleDiscardLocalChanges,
    handleSaveAsNew,
    handleJsonSave,
    handleCopyWorkflowId,
  } = persistence;

  useEditorKeyboardShortcuts({
    onUndo: editor.undo,
    onRedo: editor.redo,
    onCopy: editor.copySelected,
    onCut: editor.cutSelected,
    onPaste: editor.paste,
  });

  useEffect(() => {
    setWorkflowNameDraft(workflow.name);
  }, [workflow.name]);

  useEffect(() => {
    if (!editor.isDirty) return undefined;
    const timer = setTimeout(() => saveCachedGraph(workflowId, editor.graph), AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [editor.graph, editor.isDirty, workflowId]);

  // --- Page-level handlers ---

  const handleAddNodeAtPosition = useCallback(
    (actionId: string, screenX: number, screenY: number, parentStepId?: string) => {
      const position = canvasRef.current?.screenToFlowPosition({ x: screenX, y: screenY }) ?? {
        x: 0,
        y: 0,
      };
      actions.addNodeAtPosition(actionId, position, parentStepId);
    },
    [actions],
  );

  const handleReorganize = useCallback(() => {
    editor.setSkipUndoOnce();
    const currentWorkflow = xyflowToWorkflow(workflow.name, editor.graph);
    editor.applyCanvasChange(workflowToXyflow(currentWorkflow));
    setTimeout(() => canvasRef.current?.fitView(), FIT_VIEW_DELAY_MS);
  }, [editor, workflow.name]);

  const handleNodeContextMenu = useCallback(
    (x: number, y: number, nodeId: string) => setContextMenu({ x, y, nodeId, edgeId: null }),
    [],
  );

  const handleEdgeContextMenu = useCallback(
    (x: number, y: number, edgeId: string) => setContextMenu({ x, y, nodeId: null, edgeId }),
    [],
  );

  const handlePaneContextMenu = useCallback(
    (x: number, y: number) => setContextMenu({ x, y, nodeId: null, edgeId: null }),
    [],
  );

  const handleCloseContextMenu = useCallback(() => setContextMenu(null), []);

  const handleSidebarTabChange = useCallback((tab: SidebarTab) => {
    setSidebarTab((prev) => {
      if (prev === 'runner' && tab !== 'runner') setStepRunColors(null);
      return tab;
    });
  }, []);

  const handleToggleRunnerTab = useCallback(
    () => handleSidebarTabChange(sidebarTab === 'runner' ? 'editor' : 'runner'),
    [handleSidebarTabChange, sidebarTab],
  );

  const handleWorkflowNameCommit = useCallback(
    (next: string) => {
      setWorkflow((prev) => ({ ...prev, name: next }));
      setWorkflowNameDraft(next);
      editor.setIsDirty(true);
    },
    [editor, setWorkflow],
  );

  const handleBackClick = useCallback(() => {
    if (editor.isDirty && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    onBack?.();
  }, [editor.isDirty, onBack]);

  const handleToggleActionPalette = useCallback(() => setShowActionPalette((v) => !v), []);
  const handleToggleViewMode = useCallback(
    () => setViewMode((m) => (m === 'flow' ? 'dataflow' : 'flow')),
    [],
  );
  const handleToggleFormatMode = useCallback(
    () => setFormatMode((m) => (m === 'formatted' ? 'raw' : 'formatted')),
    [],
  );

  // --- Derived state ---

  // Validation runs every graph change and the resulting object reference
  // propagates through ValidationContext, forcing every WorkflowNode to
  // re-render. During a drag, that flood of re-renders is the dominant cost.
  // Defer the graph used for validation so React can keep the drag responsive
  // and recompute validation in a low-priority pass once the drag settles.
  const deferredGraph = useDeferredValue(editor.graph);
  const validation = useMemo(
    () => validateWorkflow(deferredGraph, availableActions),
    [deferredGraph, availableActions],
  );

  const workflowCallbacks = useMemo(
    () => ({ onRenameStep: actions.renameStep, onAddNodeAfter: actions.addNodeAfter }),
    [actions.renameStep, actions.addNodeAfter],
  );

  const selectedNode = useMemo<WorkflowNode | null>(
    () => editor.graph.nodes.find((node) => node.id === selectedNodeId) ?? null,
    [editor.graph.nodes, selectedNodeId],
  );

  return (
    <StyledEditorLayout>
      <EditorHeader
        workflowId={workflowId}
        workflowNameDraft={workflowNameDraft}
        workflowName={workflow.name}
        isDirty={editor.isDirty}
        isSaving={isSaving}
        isRunnerTabActive={sidebarTab === 'runner'}
        onBackClick={handleBackClick}
        onNameDraftChange={setWorkflowNameDraft}
        onNameCommit={handleWorkflowNameCommit}
        onCopyWorkflowId={handleCopyWorkflowId}
        onOpenJsonEditor={openJsonEditor}
        onToggleRunnerTab={handleToggleRunnerTab}
        onSaveAsNew={handleSaveAsNew}
      />
      <EditorToolbar
        isDirty={editor.isDirty}
        viewMode={viewMode}
        formatMode={formatMode}
        edgeType={edgeType}
        showActionPalette={showActionPalette}
        validationIssues={validation.issues}
        onUndo={editor.undo}
        onRedo={editor.redo}
        onCut={editor.cutSelected}
        onCopy={editor.copySelected}
        onPaste={editor.paste}
        onToggleActionPalette={handleToggleActionPalette}
        onAutoLayout={handleReorganize}
        onToggleViewMode={handleToggleViewMode}
        onToggleFormatMode={handleToggleFormatMode}
        onEdgeTypeChange={setEdgeType}
        onReset={handleReset}
        onDiscardLocalChanges={handleDiscardLocalChanges}
        onSelectNode={setSelectedNodeId}
      />
      <EditorBody
        showActionPalette={showActionPalette}
        isRunningJob={isRunningJob}
        viewMode={viewMode}
        edgeType={edgeType}
        formatMode={formatMode}
        canvasRef={canvasRef}
        graph={editor.graph}
        validation={validation}
        stepRunColors={stepRunColors}
        workflowCallbacks={workflowCallbacks}
        availableActions={availableActions}
        selectedNode={selectedNode}
        contextMenu={contextMenu}
        workflowId={workflowId}
        sidebarTab={sidebarTab}
        runnerState={runnerState}
        selectedRunnerFile={selectedRunnerFile}
        onApplyCanvasChange={editor.applyCanvasChange}
        onNodeSelected={setSelectedNodeId}
        onSelectionChange={editor.setSelectedIds}
        onNodesDeleted={actions.cleanupAfterDelete}
        onNodeContextMenu={handleNodeContextMenu}
        onEdgeContextMenu={handleEdgeContextMenu}
        onPaneContextMenu={handlePaneContextMenu}
        onEdgeDoubleClick={actions.deleteEdge}
        onNodeDragStart={editor.beginDrag}
        onNodeDragStop={editor.endDrag}
        onConnectInputBinding={actions.setInputBinding}
        onCloseContextMenu={handleCloseContextMenu}
        onDuplicateNode={actions.duplicateNode}
        onDeleteNode={actions.deleteNode}
        onDeleteEdge={actions.deleteEdge}
        onAddNodeAtPosition={handleAddNodeAtPosition}
        allNodes={editor.graph.nodes}
        allEdges={editor.graph.edges}
        onSidebarTabChange={handleSidebarTabChange}
        onNodeChange={actions.updateNode}
        onRenameStep={actions.renameStep}
        onEdgeDataChange={actions.updateEdgeData}
        onAddEdge={actions.addEdge}
        onRunnerStateChange={setRunnerState}
        onSelectedRunnerFileChange={setSelectedRunnerFile}
        onStepColorsChange={setStepRunColors}
        onIsRunningChange={setIsRunningJob}
      />
      <EditorStatusBar error={error} status={statusMessage} />
      {showJsonEditor && (
        <RawJsonEditorModal
          workflow={xyflowToWorkflow(workflow.name, editor.graph)}
          onSave={handleJsonSave}
          onClose={closeJsonEditor}
        />
      )}
    </StyledEditorLayout>
  );
};

const MemoWorkflowEditorPage = React.memo(WorkflowEditorPage);

export { MemoWorkflowEditorPage as WorkflowEditorPage };
export default MemoWorkflowEditorPage;
